import { localeOf } from "./locale.js";
import { formatMoney } from "./money.js";
import { withCountry } from "./public-profile.js";
/**
 * WHAT THE PROFILE COMPUTES (spec 2026-10-20 §5), as plain functions: the
 * web page `/t/<slug>` and the phone's own profile screen only draw. The
 * paths are the Worker's doors on the web origin -- the one place a stranger
 * reads a profile from, the phone included: the API answers them only behind
 * the Worker.
 */
/** «B/. 12.00», «Desde B/. 8.00», or null when no price is live. */
export function cardPrice(item, country) {
    if (item.fromPriceCents === null || item.priceCount === 0)
        return null;
    const price = formatMoney(item.fromPriceCents, localeOf(country));
    return item.priceCount > 1 ? `Desde ${price}` : price;
}
/** A service's price: what it costs at least -- the work may need more. Null when no price is live. */
export function servicePrice(item, country) {
    if (item.fromPriceCents === null || item.priceCount === 0)
        return null;
    return `Desde ${formatMoney(item.fromPriceCents, localeOf(country))}`;
}
/** The profile's WhatsApp greeting: it says where the visitor comes from, so the business knows the profile brought them. */
export const PROFILE_WHATSAPP_GREETING = "Hola, te escribo desde tu perfil en VozQI.";
/** A pin as Google Maps and Waze read it: «8.983333,-79.516667». */
export function pinQuery(pin) {
    return `${String(pin.lat)},${String(pin.lng)}`;
}
/** The Maps Embed API's page for an iframe, at `query`, already encoded. */
export function mapsEmbedSrc(key, query) {
    return `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(key)}&q=${query}`;
}
/**
 * «Cómo llegar» in Waze: only for a business that attends in a shop, whose
 * street and pin are public. To the owner's pin when there is one, else to
 * the street and zone, which Waze looks up.
 */
export function wazeHref(profile) {
    if (profile.pin !== null)
        return `https://waze.com/ul?ll=${pinQuery(profile.pin)}&navigate=yes`;
    if (profile.street === null)
        return null;
    const address = profile.zone === null ? profile.street : `${profile.street}, ${profile.zone}`;
    return `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;
}
export function profileMapsOf(shop, key) {
    const { street, zone, pin } = shop.profile;
    const query = pin !== null
        ? pinQuery(pin)
        : street !== null
            ? encodeURIComponent(`${shop.business.name}, ${street}, ${withCountry(zone, shop.business.country)}`)
            : null;
    if (query === null)
        return null;
    return {
        embed: key === undefined || key === "" ? null : mapsEmbedSrc(key, query),
        open: `https://www.google.com/maps/search/?api=1&query=${query}`,
    };
}
export function profileLayoutOf(profile) {
    const { services } = profile.kinds;
    const products = profile.kinds.products || !services;
    return { services, products, shopWord: products && !services };
}
/** The kind the products part asks for: PRODUCT beside a services list, everything when it is the whole shop. */
export function productsKindOf(layout) {
    return layout.services ? "PRODUCT" : null;
}
/** The sections a part lists: only those holding its kind, so no category opens empty. */
export function sectionsHolding(sections, kind) {
    if (kind === null)
        return sections;
    return sections.filter((section) => (kind === "SERVICE" ? section.kinds.services : section.kinds.products));
}
/**
 * The cards with a heading wherever the section changes. The server's order
 * IS the grouping (sections in order, the unsectioned last), so this walks
 * every page loaded so far as one list: a section cut by a page boundary is
 * headed once. A shop without sections gets no headings; one with sections
 * heads its unsectioned tail «Otros».
 */
export function withSectionHeaders(items, sections) {
    const names = new Map(sections.map((section) => [section.id, section.name]));
    const rows = [];
    let current;
    for (const item of items) {
        if (sections.length > 0 && item.sectionId !== current) {
            current = item.sectionId;
            const name = current === null ? "Otros" : (names.get(current) ?? "Otros");
            rows.push({ kind: "section", key: `section-${current ?? "none"}`, name });
        }
        rows.push({ kind: "item", key: item.token, item });
    }
    return rows;
}
/**
 * The query of one page of cards; each part only when there is one, in the
 * Worker's own order (cursor, q, section, kind), so the same page is one cache entry.
 */
export function shopPageQuery(cursor, q, section, kind) {
    const params = new URLSearchParams();
    if (cursor !== null)
        params.set("cursor", cursor);
    const text = q.trim();
    if (text !== "")
        params.set("q", text);
    if (section !== null)
        params.set("section", section);
    if (kind !== null)
        params.set("kind", kind);
    return params.toString();
}
/** The profile's own data, through the Worker. */
export function shopDataUrl(slug) {
    return `/t/api/${slug}`;
}
/** One page of cards from the Worker. */
export function shopItemsUrl(slug, cursor, q, section, kind) {
    const query = shopPageQuery(cursor, q, section, kind);
    return `/t/api/${slug}/items${query === "" ? "" : `?${query}`}`;
}
/** The business's logo on its profile. */
export function shopLogoUrl(slug) {
    return `/t/${slug}/logo`;
}
/**
 * The profile's cover photo, through the Worker. `version` names the stored
 * photo, so replacing it changes the address: the Worker keeps each
 * version's bytes for good, and a change never waits on an expired copy.
 */
export function shopCoverUrl(slug, version) {
    return `/t/${slug}/portada/${version}`;
}
/**
 * A card's cover, through the shop: the Worker checks it against the page
 * that listed it (`pageQuery`), one cached lookup for the whole page.
 */
export function coverSrc(slug, item, pageQuery) {
    if (item.coverPhotoId === null)
        return null;
    return `/t/${slug}/foto/${item.coverPhotoId}${pageQuery === "" ? "" : `?${pageQuery}`}`;
}
/** An item's own page data (`/p/<token>`), through the Worker. */
export function itemDataUrl(token) {
    return `/p/api/${token}`;
}
/** An item's photo, through the Worker's own path: a page never sees a storage address. */
export function itemPhotoSrc(token, attachmentId, size) {
    return `/p/${token}/foto/${attachmentId}${size === "thumb" ? "?t=thumb" : ""}`;
}
